import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LayoutTemplate, X, Save, Trash2, Share2, Download, Loader2, Check, Globe } from "lucide-react";
import { toast } from "sonner";
import { encodeTemplate, decodeTemplate } from "@/lib/templateCode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Templates — full chart configuration saved per account in MongoDB
 * (multi-device). Shareable via compact copy/paste codes.
 */
export default function TemplatesMenu({ getSnapshot, onApply }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [importing, setImporting] = useState(false);
  const [importCode, setImportCode] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const ref = useRef(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/templates`);
      let list = r.data.templates || [];
      // One-time migration from localStorage (legacy free storage)
      try {
        const legacy = JSON.parse(localStorage.getItem("qtr-chart-templates") || "[]");
        if (legacy.length && !localStorage.getItem("qtr-templates-migrated")) {
          for (const t of legacy) {
            const { id, name: n, createdAt, ...payload } = t;
            await axios.post(`${API}/templates`, { name: n, payload });
          }
          localStorage.setItem("qtr-templates-migrated", "1");
          const r2 = await axios.get(`${API}/templates`);
          list = r2.data.templates || [];
          toast.success(`${legacy.length} template(s) migrés vers votre compte`);
        }
      } catch { /* noop */ }
      setTemplates(list);
    } catch {
      toast.error("Impossible de charger les templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const saveCurrent = async () => {
    if (!name.trim()) return;
    try {
      const r = await axios.post(`${API}/templates`, { name: name.trim(), payload: getSnapshot() });
      setTemplates((prev) => [r.data, ...prev]);
      setNaming(false);
      setName("");
      toast.success(`Template « ${r.data.name} » sauvegardé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la sauvegarde");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const share = async (t) => {
    try {
      await navigator.clipboard.writeText(encodeTemplate(t));
      setCopiedId(t.id);
      setTimeout(() => setCopiedId(null), 1500);
      toast.success("Code copié — partagez-le à un autre trader !");
    } catch {
      toast.error("Copie impossible");
    }
  };

  const publish = async (t) => {
    try {
      await axios.post(`${API}/gallery/publish`, { template_id: t.id, description: "" });
      toast.success(`« ${t.name} » publié dans la galerie communautaire !`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publication impossible");
    }
  };

  const doImport = async () => {
    try {
      const { name: n, payload } = decodeTemplate(importCode);
      const r = await axios.post(`${API}/templates`, { name: n, payload });
      setTemplates((prev) => [r.data, ...prev]);
      setImporting(false);
      setImportCode("");
      toast.success(`Template « ${n} » importé`);
    } catch {
      toast.error("Code invalide — vérifiez le copier/coller (doit commencer par PTPL1.)");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="chip text-xs flex items-center gap-1.5 hover:bg-white/5"
        data-testid="chart-templates-btn"
        title="Templates — sauvegarder/charger/partager votre configuration complète"
      >
        <LayoutTemplate size={12} /> Templates
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-[60] panel p-3 w-80" data-testid="chart-templates-menu">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-white/60">Mes Templates</div>
            <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white"><X size={13} /></button>
          </div>

          {loading ? (
            <div className="grid place-items-center py-6 text-white/40"><Loader2 size={16} className="animate-spin" /></div>
          ) : (
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {templates.length === 0 && (
                <div className="text-[11px] text-white/40 py-3 text-center">
                  Aucun template. Configurez votre graphique puis sauvegardez — ou importez un code partagé.
                </div>
              )}
              {templates.map((t) => (
                <div key={t.id} className="flex items-center gap-1">
                  <button
                    onClick={() => { onApply({ ...t.payload }); setOpen(false); toast.success(`Template « ${t.name} » appliqué`); }}
                    className="flex-1 min-w-0 flex flex-col items-start px-2 py-1.5 rounded-md border border-white/10 hover:border-accent-blue hover:bg-white/5 text-left"
                    data-testid={`template-apply-${t.id}`}
                  >
                    <span className="text-[11px] font-medium truncate w-full">{t.name}</span>
                    <span className="text-[9px] text-white/40">
                      {(t.payload?.indicators || []).length} indicateurs · {t.created_at ? new Date(t.created_at).toLocaleDateString("fr-FR") : ""}
                    </span>
                  </button>
                  <button onClick={() => share(t)} className="text-white/40 hover:text-accent-blue px-1" title="Partager (copier le code)" data-testid={`template-share-${t.id}`}>
                    {copiedId === t.id ? <Check size={12} className="text-up" /> : <Share2 size={12} />}
                  </button>
                  <button onClick={() => publish(t)} className="text-white/40 hover:text-emerald-400 px-1" title="Publier dans la galerie communautaire" data-testid={`template-publish-${t.id}`}>
                    <Globe size={12} />
                  </button>
                  <button onClick={() => remove(t.id)} className="text-white/40 hover:text-red-400 px-1" aria-label="Supprimer" data-testid={`template-del-${t.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {naming ? (
            <div className="mt-2 space-y-1.5" data-testid="template-name-form">
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveCurrent(); if (e.key === "Escape") setNaming(false); }}
                placeholder="Nom du template (ex: Setup SMC Londres)"
                maxLength={60}
                className="w-full bg-hover border border-white/10 rounded-md px-2 py-1.5 text-[11px] focus:border-accent-blue outline-none"
                data-testid="template-name-input"
              />
              <div className="flex gap-1.5">
                <button onClick={saveCurrent} disabled={!name.trim()} className="flex-1 text-[11px] py-1 rounded-md bg-emerald-600/80 hover:bg-emerald-600 disabled:opacity-40" data-testid="template-name-confirm">
                  Sauvegarder
                </button>
                <button onClick={() => { setNaming(false); setName(""); }} className="text-[11px] px-3 py-1 rounded-md border border-white/10">Annuler</button>
              </div>
            </div>
          ) : importing ? (
            <div className="mt-2 space-y-1.5" data-testid="template-import-form">
              <textarea
                autoFocus
                value={importCode}
                onChange={(e) => setImportCode(e.target.value)}
                placeholder="Collez le code partagé (PTPL1.xxxx…)"
                rows={3}
                className="w-full bg-hover border border-white/10 rounded-md px-2 py-1.5 text-[10px] font-mono focus:border-accent-blue outline-none resize-none"
                data-testid="template-import-input"
              />
              <div className="flex gap-1.5">
                <button onClick={doImport} disabled={!importCode.trim()} className="flex-1 text-[11px] py-1 rounded-md bg-accent-blue/80 hover:bg-accent-blue disabled:opacity-40" data-testid="template-import-confirm">
                  Importer
                </button>
                <button onClick={() => { setImporting(false); setImportCode(""); }} className="text-[11px] px-3 py-1 rounded-md border border-white/10">Annuler</button>
              </div>
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              <button
                onClick={() => setNaming(true)}
                className="flex items-center justify-center gap-1.5 text-[11px] py-1.5 border border-dashed border-white/15 hover:border-emerald-500/50 hover:bg-emerald-500/5 rounded-md text-white/70 hover:text-emerald-300"
                data-testid="template-save-btn"
              >
                <Save size={11} /> Sauvegarder
              </button>
              <button
                onClick={() => setImporting(true)}
                className="flex items-center justify-center gap-1.5 text-[11px] py-1.5 border border-dashed border-white/15 hover:border-accent-blue/50 hover:bg-accent-blue/5 rounded-md text-white/70 hover:text-accent-blue"
                data-testid="template-import-btn"
              >
                <Download size={11} /> Importer un code
              </button>
            </div>
          )}
          <div className="text-[9px] text-white/35 mt-1.5 leading-relaxed">
            Synchronisés avec votre compte (multi-appareils). Partagez un setup via son code PTPL1.
          </div>
          <Link to="/app/gallery" onClick={() => setOpen(false)} className="mt-1.5 flex items-center justify-center gap-1.5 text-[10px] py-1 rounded-md text-accent-blue hover:bg-accent-blue/10" data-testid="template-gallery-link">
            <Globe size={10} /> Voir la galerie communautaire
          </Link>
        </div>
      )}
    </div>
  );
}
